import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  MATCH_PAIRS,
  ROUND_REPEATS_MAX,
  canonicalForm,
  stageWarmUp,
  checkAnswer,
  checkTile,
  newId,
  nowSeconds,
  type CardForm,
  type RoundKey,
  type Verdict,
} from "@vocifuchs/shared";
import { api, ApiError, noAnswer } from "../lib/api.js";
import {
  dropRound,
  flushRounds,
  keepRound,
  laterMayWork,
  sendRound,
  type RoundBody,
  type RoundSummary,
} from "../lib/outbox.js";
import { useSession } from "../session.js";
import {
  keepRoundCards,
  keptRoundCards,
  roundsChanged,
  sessionBody,
  type PracticeCard,
  type SessionReply,
} from "../lib/offline.js";
import { common } from "../copy/common.js";
import { errors } from "../copy/errors.js";
import { practice } from "../copy/practice.js";
import { speak } from "../lib/text.js";
import { getSettings } from "../lib/settings.js";
import { SpeakButton } from "../components/SpeakButton.js";
import { Fox } from "../components/Fox.js";
import { MatchBoard, type MatchAnswer } from "../components/MatchBoard.js";
import { Banner, Button, Card, inputClass } from "../components/ui.js";

interface Result {
  term_id: string;
  direction: "ab" | "ba";
  verdict: Verdict;
  attempt: number;
  form: CardForm;
}

/** `practiceResultsInput` refuses a longer round; a forgotten tab must not cost the answers. */
const MAX_ROUND_SECONDS = 3600;

/** Answers on their way to the worker, and where to go once they arrived. */
interface Save {
  body: RoundBody;
  then: "summary" | "leave";
  state: "saving" | "failed";
  message?: string;
}

function cardKey(card: { term_id: string; direction: "ab" | "ba" }): string {
  return `${card.term_id}:${card.direction}`;
}

/** Each missed card once, in the order it was first missed. */
function missedCards(all: Result[], cards: PracticeCard[]): PracticeCard[] {
  const byKey = new Map(cards.map((card) => [cardKey(card), card]));
  const seen = new Set<string>();
  const out: PracticeCard[] = [];
  for (const result of all) {
    const key = cardKey(result);
    if (result.verdict !== "wrong" || seen.has(key)) continue;
    seen.add(key);
    const card = byKey.get(key);
    if (card) out.push(card);
  }
  return out;
}

const TONE: Record<Verdict, string> = {
  correct: "border-moss/30 bg-moss/10 text-moss",
  almost: "border-amber/30 bg-amber/10 text-amber",
  wrong: "border-rose/30 bg-rose/10 text-rose",
};

export function PracticeScreen() {
  // One list, «heute dran» in one language across all lists, or «Wiederholen»
  // over the lists in the address (issue #101), which «Nochmals» keeps.
  const { listId, lang } = useParams();
  const dueLang = lang === "en" || lang === "fr" ? lang : null;
  const [search] = useSearchParams();
  const reviewLists = listId || dueLang ? "" : (search.get("lists") ?? "");
  const navigate = useNavigate();
  const [queue, setQueue] = useState<PracticeCard[] | null>(null);
  const [index, setIndex] = useState(0);
  const [results, setResults] = useState<Result[]>([]);
  const [typed, setTyped] = useState("");
  const [feedback, setFeedback] = useState<{
    verdict: Verdict;
    canonical: string;
    results: Result[];
  } | null>(null);
  const [save, setSave] = useState<Save | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<RoundSummary | null>(null);
  /** Finished, but the answers wait on this device for a connection (issue #118). */
  const [queued, setQueued] = useState(false);
  const { user } = useSession();
  const userId = user?.id;
  // Answers on the board that is still open. They join `results` when the
  // board is finished, but a cancel before that must still send them.
  // A ref, not state: the board reports its last pair and finishes in the
  // same tick, and a state read in finishBoard would still miss that pair.
  const boardAnswers = useRef<Result[]>([]);
  /** Words missed at least once, shown again at the end (issue #46). */
  const [missed, setMissed] = useState<PracticeCard[]>([]);
  /** Words in the round, fixed at the start: a repeated card is not a new word (#45). */
  const [roundSize, setRoundSize] = useState(0);
  const [started] = useState(() => Date.now());
  const [title, setTitle] = useState<string>(practice.defaultTitle);
  /** The round opens on a board of five new words, when it has them. */
  const [warmUp, setWarmUp] = useState(false);
  /** «Kacheln zeigen» on the card that is up: tiles for this one card only. */
  const [helped, setHelped] = useState(false);

  useEffect(() => {
    const listIds = reviewLists.split(",").filter(Boolean);
    if (!listId && !dueLang && listIds.length === 0) {
      setQueue([]);
      return;
    }
    // «heute dran» and a single list can be kept on the device for practising
    // offline (issue #120); «Wiederholen» cannot.
    const key: RoundKey | null = dueLang ? `due:${dueLang}` : listId ? `list:${listId}` : null;
    const body = key ? sessionBody(key) : { mode: "review", list_ids: listIds, size: 10 };
    const start = (data: SessionReply) => {
      const lang = common.lang[data.lang_b];
      setTitle(
        data.title ??
          (listIds.length > 0
            ? practice.reviewTitle(lang)
            : data.extra
              ? practice.extraTitle(lang)
              : practice.dueTitle(lang)),
      );
      const staged = stageWarmUp(data.cards);
      setWarmUp(staged !== null);
      setQueue(staged ?? data.cards);
      setRoundSize(new Set(data.cards.map(cardKey)).size);
    };
    void api<SessionReply>("/api/practice/session", { method: "POST", body: JSON.stringify(body) })
      .then((data) => {
        if (key && userId) keepRoundCards(userId, key, data);
        start(data);
      })
      .catch((err: unknown) => {
        // Also when the worker is down: the note above Start says practising works.
        const offline = noAnswer(err);
        const kept = offline && key && userId ? keptRoundCards(userId, key) : null;
        if (kept) start(kept);
        else setError(offline ? practice.notKept : err instanceof ApiError ? err.message : errors.failed);
      });
    // The user id, not the user: a refresh brings a new object, and a new
    // round in the middle of this one would throw its answers away.
  }, [listId, dueLang, reviewLists, userId]);

  const cards = queue ?? [];
  const card = cards[index];
  const total = roundSize;
  const attemptsForCard = useMemo(
    () =>
      card
        ? results.filter((r) => r.term_id === card.term_id && r.direction === card.direction)
            .length + 1
        : 1,
    [results, card],
  );

  // The app picks the form, card by card (issue #63): the warm-up board first,
  // then whatever the box asks for, or tiles after «Kacheln zeigen».
  const matchGroup = useMemo(
    () => (warmUp && index === 0 ? cards.slice(0, MATCH_PAIRS) : []),
    [warmUp, cards, index],
  );
  const inMatch = matchGroup.length === MATCH_PAIRS;
  const cardForm: CardForm = helped ? "choice" : card?.mode ?? "type";
  // Counts words, not cards: after a wrong answer «3 / 10» stays «3 / 10»
  // instead of turning into «3 / 11» (issue #45).
  const position = Math.min(
    // The warm-up board counts its words once it is done, not before the
    // first tap: a round must not open half full.
    new Set(cards.slice(0, index + (inMatch ? 0 : 1)).map(cardKey)).size,
    total,
  );

  // «Automatisch vorlesen»: the foreign word once per card. A card asked in
  // the foreign language is read when it appears; one asked in German is read
  // after the answer, when the solution is on the screen (see grade).
  const shownCard = card && !inMatch ? `${index}:${card.term_id}:${card.direction}` : null;
  useEffect(() => {
    if (!shownCard || !card || card.lang_prompt === "de") return;
    if (getSettings().autoSpeak) speak(card.prompt, card.lang_prompt);
    // Keyed on the card, not on every render of the same card.
  }, [shownCard]);

  const showTiles = () => {
    setHelped(true);
    setTyped("");
  };

  // A failed save used to be silent: the last card came back and the round's
  // answers were gone. Now the round waits on the device and is sent once
  // there is a connection again (issue #118); only where the device cannot
  // keep it does the screen offer to send again.
  const submit = async (body: RoundBody, then: Save["then"]) => {
    setSave({ body, then, state: "saving" });
    const kept = user ? await keepRound(user.id, body) : false;
    // Older rounds first: a streak day before the day after it.
    if (user) await flushRounds(user.id, body.round_id);
    try {
      const summary = await sendRound(body);
      if (kept) await dropRound(body.round_id);
      roundsChanged();
      if (then === "leave") {
        navigate("/");
        return;
      }
      setSave(null);
      setMissed(missedCards(body.results, cards));
      setDone(summary);
    } catch (err) {
      if (kept && laterMayWork(err)) {
        if (then === "leave") {
          navigate("/");
          return;
        }
        setSave(null);
        setMissed(missedCards(body.results, cards));
        setQueued(true);
        return;
      }
      if (kept) await dropRound(body.round_id);
      setSave({
        body,
        then,
        state: "failed",
        message: err instanceof ApiError && err.status > 0 && err.status < 500 ? err.message : undefined,
      });
    }
  };

  /** The round as it goes out, named once: a resend is the same round (issue #118). */
  const roundBody = (all: Result[], then: Save["then"]): RoundBody => ({
    list_id: listId ?? null,
    seconds: Math.min(MAX_ROUND_SECONDS, Math.round((Date.now() - started) / 1000)),
    results: all,
    // A cancelled round keeps its answers but is not a streak day.
    complete: then === "summary",
    round_id: newId(),
    played_at: nowSeconds(),
  });

  const finish = (all: Result[]) => submit(roundBody(all, "summary"), "summary");

  // «Abbrechen jederzeit ohne Verlust» (docs/UX.md §2): what was answered so
  // far still counts.
  const cancel = () => {
    // After a right answer a timer is about to move on and would save a
    // second time; a wrong answer waits for a tap, so leaving there is fine.
    if (save || (feedback && feedback.verdict !== "wrong")) return;
    const all = [...results, ...boardAnswers.current];
    if (all.length === 0) navigate("/");
    else void submit(roundBody(all, "leave"), "leave");
  };

  const recordBoardAnswer = (answer: MatchAnswer) => {
    boardAnswers.current.push({ ...answer, form: "match" });
  };

  const finishBoard = () => {
    const all = [...results, ...boardAnswers.current];
    setResults(all);
    boardAnswers.current = [];
    const next = index + MATCH_PAIRS;
    if (next >= cards.length) void finish(all);
    else setIndex(next);
  };

  const advance = (verdict: Verdict, nextResults: Result[]) => {
    if (!card) return;
    setFeedback(null);
    setTyped("");
    setHelped(false);
    let nextQueue = cards;
    // A wrong card comes back a few places later, but only so often: after
    // that it waits for tomorrow instead of stretching the round (issue #45).
    if (verdict === "wrong" && attemptsForCard <= ROUND_REPEATS_MAX) {
      const insertAt = Math.min(index + 4, cards.length);
      nextQueue = [...cards.slice(0, insertAt), card, ...cards.slice(insertAt)];
      setQueue(nextQueue);
    }
    if (index + 1 >= nextQueue.length) {
      void finish(nextResults);
    } else {
      setIndex((i) => i + 1);
    }
  };

  const grade = (answer: string) => {
    if (!card || feedback) return;
    // A tile is right or wrong, never «Fast» (issue #65).
    const checked =
      cardForm === "choice" ? checkTile(answer, card.solution) : checkAnswer(answer, card.solution);
    const nextResults = [
      ...results,
      {
        term_id: card.term_id,
        direction: card.direction,
        verdict: checked.verdict,
        attempt: attemptsForCard,
        form: cardForm,
      },
    ];
    setResults(nextResults);
    setFeedback({ verdict: checked.verdict, canonical: checked.canonical, results: nextResults });
    const readSolution = getSettings().autoSpeak && card.lang_answer !== "de";
    if (readSolution) speak(checked.canonical, card.lang_answer);

    // A wrong answer waits for a tap: the solution is the one thing worth
    // reading on this card, and 1.4 s was too short for that (issue #46).
    if (checked.verdict === "wrong") return;
    window.setTimeout(
      () => advance(checked.verdict, nextResults),
      // Long enough to hear the word before the next card cuts it off.
      checked.verdict === "almost" ? 1600 : readSolution ? 1500 : 800,
    );
  };

  if (error) {
    return (
      <div className="mx-auto max-w-xl space-y-4">
        <Banner>{error}</Banner>
        <Link to="/" className="font-semibold text-fox-ink underline-offset-4 hover:underline">
          {common.backToStart}
        </Link>
      </div>
    );
  }

  if (save) {
    const failed = save.state === "failed";
    return (
      <div className="mx-auto max-w-xl">
        <Card className="flex flex-col items-center gap-5 px-6 py-10 text-center sm:py-14">
          <Fox mood="think" className="h-20 w-20 sm:h-24 sm:w-24" />
          <p aria-live="polite" className="text-xl sm:text-2xl">
            {failed ? practice.saveFailed : practice.saving}
          </p>
          {failed && save.message ? <Banner>{save.message}</Banner> : null}
          {failed ? (
            <div className="mt-2 flex w-full flex-col gap-3 sm:flex-row">
              <Button onClick={() => void submit(save.body, save.then)}>{practice.retry}</Button>
              <Button variant="ghost" onClick={() => navigate("/")}>
                {practice.leaveUnsaved}
              </Button>
            </div>
          ) : null}
        </Card>
      </div>
    );
  }

  if (done || queued) {
    return (
      <div className="mx-auto max-w-xl">
        <Card className="flex flex-col items-center gap-5 px-6 py-10 text-center sm:py-14">
          <Fox mood="happy" className="h-24 w-24 sm:h-28 sm:w-28" />
          <h1 className="text-3xl sm:text-4xl">{practice.roundDone}</h1>
          {/* Praise the work, never the person (docs/UX.md §9). */}
          {done ? (
            <ul className="space-y-1 text-xl text-ink-soft sm:text-2xl">
              <li>{practice.firstTry(done.first_try, done.words)}</li>
              {done.newly_sitting > 0 ? (
                <li className="font-semibold text-moss">{practice.newlySitting(done.newly_sitting)}</li>
              ) : null}
              {done.streak >= 2 ? <li>{practice.streak(done.streak)}</li> : null}
            </ul>
          ) : (
            // The counts come from the worker; until the round arrives there are none.
            <p className="text-lg text-ink-soft sm:text-xl">{practice.queued}</p>
          )}
          {missed.length > 0 ? (
            <section className="w-full space-y-2 text-left">
              <h2 className="text-lg sm:text-xl">{practice.missedTitle}</h2>
              <ul className="space-y-2">
                {missed.map((card) => {
                  const solution = canonicalForm(card.solution);
                  // Read the foreign side, whichever way the card was asked.
                  const foreign =
                    card.lang_answer !== "de"
                      ? { text: solution, lang: card.lang_answer }
                      : { text: card.prompt, lang: card.lang_prompt };
                  return (
                    <li
                      key={cardKey(card)}
                      className="flex items-center justify-between gap-3 rounded-2xl border border-line/60 bg-paper-3 px-4 py-2"
                    >
                      <span className="min-w-0">
                        <span lang={card.lang_prompt} className="block text-ink-soft">
                          {card.prompt}
                        </span>
                        <span lang={card.lang_answer} className="block font-display text-xl break-words">
                          {solution}
                        </span>
                      </span>
                      <SpeakButton
                        text={foreign.text}
                        lang={foreign.lang}
                        label={practice.speakWord(foreign.text)}
                        size="sm"
                      />
                    </li>
                  );
                })}
              </ul>
            </section>
          ) : null}
          <div className="mt-2 flex w-full flex-col gap-3 sm:flex-row">
            <Button onClick={() => window.location.reload()}>{practice.again}</Button>
            <Button variant="ghost" onClick={() => navigate("/")}>
              {practice.done}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  if (queue && !card) {
    return (
      <div className="mx-auto max-w-xl space-y-4">
        <p className="text-ink-soft">{practice.empty}</p>
        <Link to="/" className="font-semibold text-fox-ink underline-offset-4 hover:underline">
          {common.backToStart}
        </Link>
      </div>
    );
  }

  if (!card) return <p className="text-ink-soft">{common.loading}</p>;

  return (
    <div className="mx-auto max-w-2xl space-y-5">
      <div className="flex items-center justify-between gap-4 text-sm text-ink-soft">
        <button
          type="button"
          className="min-h-11 font-semibold underline-offset-4 hover:underline"
          onClick={cancel}
        >
          {practice.cancel}
        </button>
        <span className="truncate">{title}</span>
        <span className="shrink-0 tabular-nums">
          {position} / {total}
        </span>
      </div>

      <div
        className="h-2 overflow-hidden rounded-full bg-paper-2"
        role="progressbar"
        aria-valuenow={position}
        aria-valuemin={0}
        aria-valuemax={total}
        aria-label={practice.progressAria}
      >
        <div
          className="h-full rounded-full bg-fox transition-[width] duration-300"
          style={{ width: `${(position / Math.max(total, 1)) * 100}%` }}
        />
      </div>

      {inMatch ? (
        <Card className="px-5 py-7 sm:px-8 sm:py-10">
          <MatchBoard
            key={index}
            cards={matchGroup}
            onAnswer={recordBoardAnswer}
            onDone={finishBoard}
          />
        </Card>
      ) : (
        <Card className="px-5 py-7 sm:px-8 sm:py-10">
          <div className="flex items-start justify-between gap-4">
            <h1
              lang={card.lang_prompt}
              className="text-4xl leading-tight break-words sm:text-5xl lg:text-6xl"
            >
              {card.prompt}
            </h1>
            {/* Keyed: the echo of the last card's word must not light the next one. */}
            <SpeakButton
              key={cardKey(card)}
              text={card.prompt}
              lang={card.lang_prompt}
              label={practice.speakAria}
            />
          </div>

          <div className="mt-7">
            {feedback?.verdict === "wrong" ? (
              <div className="space-y-4">
                <div
                  aria-live="assertive"
                  className={`rounded-2xl border px-4 py-4 ${TONE.wrong}`}
                >
                  <p className="text-lg font-semibold sm:text-xl">{practice.notYet}</p>
                  <p className="mt-1 text-ink-soft">{practice.solutionIs}</p>
                  <div className="mt-1 flex items-center justify-between gap-3">
                    <p
                      lang={card.lang_answer}
                      className="font-display text-3xl break-words text-ink sm:text-4xl"
                    >
                      {feedback.canonical}
                    </p>
                    <SpeakButton
                      text={feedback.canonical}
                      lang={card.lang_answer}
                      label={practice.speakSolutionAria}
                    />
                  </div>
                </div>
                <div className="sm:max-w-xs">
                  <Button
                    autoFocus
                    onClick={() => advance(feedback.verdict, feedback.results)}
                  >
                    {practice.next}
                  </Button>
                </div>
              </div>
            ) : feedback ? (
              <p
                aria-live="assertive"
                className={`rounded-2xl border px-4 py-4 text-lg font-semibold sm:text-xl ${TONE[feedback.verdict]}`}
              >
                {feedback.verdict === "correct"
                  ? practice.correct
                  : practice.almost(feedback.canonical)}
              </p>
            ) : cardForm === "choice" ? (
              <div className="space-y-3">
                {/* Only for help: a new word gets tiles anyway, and nothing to explain (#97). */}
                {helped ? (
                  <p id="tiles-hint" className="text-ink-soft">
                    {practice.tilesHint}
                  </p>
                ) : null}
                <div className="grid gap-3 sm:grid-cols-2">
                  {card.choices.map((choice, slot) => (
                    <button
                      key={choice}
                      type="button"
                      // After «Kacheln zeigen» the input is gone; keep keyboard
                      // and screen reader users on the card, and tell them why: once, on
                      // the tile that gets the focus, not on all four.
                      autoFocus={helped && slot === 0}
                      aria-describedby={helped && slot === 0 ? "tiles-hint" : undefined}
                      lang={card.lang_answer}
                      className="min-h-16 rounded-2xl border-2 border-line bg-paper-2 px-4 py-3 text-left text-lg font-semibold hover:border-fox hover:bg-fox/5 active:translate-y-0.5"
                      onClick={() => grade(choice)}
                    >
                      {choice}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  grade(typed);
                }}
                className="space-y-3"
              >
                <input
                  className={`${inputClass()} text-xl`}
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  lang={card.lang_answer}
                  aria-label={practice.answerAria}
                  autoFocus
                />
                <div className="flex flex-col gap-3 sm:flex-row">
                  <div className="sm:max-w-xs sm:flex-1">
                    <Button type="submit">{practice.check}</Button>
                  </div>
                  {/* Help for this card only; it counts like a second attempt (issue #63). */}
                  <div className="sm:max-w-xs sm:flex-1">
                    <Button type="button" variant="ghost" onClick={showTiles}>
                      {practice.showTiles}
                    </Button>
                  </div>
                </div>
              </form>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}
